import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import TabsComponent from '../../../stories/atoms/tabs'
import { tabsActivationModeOptions } from '../../../stories/atoms/tabs/constants'

import {
  ProjectMembersContainer,
  ProjectMembersTabsContainer,
  ProjectMembersTabsContent,
  ProjectMembersTabTitle
} from '../../styled-components'
import MembersList from './members/members-list'
import ContactsList from './contacts/contacts-list'
import TeamsList from './team/teams-list'
import {
  projectMembersTabOptions,
  triggerVariantStyleOptions
} from '../../constants'
import { getTriggerStyle } from '../../utils'

const tabContentStyle = {
  padding: 0,
  borderRadius: '0 0 16px 16px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden'
}

const ProjectInfoPanelMembers = ({
  members = [],
  teams = [],
  contacts = [],
  isLoadingContacts = false,
  isLoadingMembers = false,
  isLoadingTeams = false
}) => {
  const { formatMessage } = useIntl()
  const [activeTab, setActiveTab] = useState(projectMembersTabOptions.MEMBERS)

  useEffect(() => {
    setActiveTab(projectMembersTabOptions.MEMBERS)
  }, [projectMembersTabOptions.MEMBERS])

  const rawTabDataByValue = useMemo(
    () => ({
      [projectMembersTabOptions.MEMBERS]: Array.isArray(members) ? members : [],
      [projectMembersTabOptions.TEAMS]: Array.isArray(teams) ? teams : [],
      [projectMembersTabOptions.CONTACTS]: Array.isArray(contacts)
        ? contacts
        : []
    }),
    [contacts, members, teams]
  )

  const isMemberActive = activeTab === projectMembersTabOptions.MEMBERS
  const isTeamActive = activeTab === projectMembersTabOptions.TEAMS
  const isContactsActive = activeTab === projectMembersTabOptions.CONTACTS

  return (
    <ProjectMembersContainer>
      <ProjectMembersTabsContainer>
        <TabsComponent.Root
          value={activeTab}
          onValueChange={setActiveTab}
          activationMode={tabsActivationModeOptions.AUTOMATIC}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0
          }}
        >
          <TabsComponent.List>
            <TabsComponent.Trigger
              value={projectMembersTabOptions.MEMBERS}
              style={getTriggerStyle(triggerVariantStyleOptions.LEFT)}
            >
              <ProjectMembersTabTitle isActive={isMemberActive}>
                {formatMessage({ id: 'members' })}{' '}
                {`(${rawTabDataByValue[projectMembersTabOptions.MEMBERS].length})`}
              </ProjectMembersTabTitle>
            </TabsComponent.Trigger>
            <TabsComponent.Trigger
              value={projectMembersTabOptions.TEAMS}
              style={getTriggerStyle(triggerVariantStyleOptions.MIDDLE)}
            >
              <ProjectMembersTabTitle isActive={isTeamActive}>
                {formatMessage({ id: 'teams_titleTeamGroups' })}{' '}
                {`(${rawTabDataByValue[projectMembersTabOptions.TEAMS].length})`}
              </ProjectMembersTabTitle>
            </TabsComponent.Trigger>
            <TabsComponent.Trigger
              value={projectMembersTabOptions.CONTACTS}
              style={getTriggerStyle(triggerVariantStyleOptions.RIGHT)}
            >
              <ProjectMembersTabTitle isActive={isContactsActive}>
                {formatMessage({ id: 'personas_contacts' })}{' '}
                {`(${rawTabDataByValue[projectMembersTabOptions.CONTACTS].length})`}
              </ProjectMembersTabTitle>
            </TabsComponent.Trigger>
          </TabsComponent.List>
          <ProjectMembersTabsContent>
            <TabsComponent.Content
              value={projectMembersTabOptions.MEMBERS}
              style={tabContentStyle}
            >
              <MembersList
                members={rawTabDataByValue[projectMembersTabOptions.MEMBERS]}
                isLoading={isLoadingMembers}
              />
            </TabsComponent.Content>
            <TabsComponent.Content
              value={projectMembersTabOptions.TEAMS}
              style={tabContentStyle}
            >
              <TeamsList
                teams={rawTabDataByValue[projectMembersTabOptions.TEAMS]}
                isLoading={isLoadingTeams}
              />
            </TabsComponent.Content>
            <TabsComponent.Content
              value={projectMembersTabOptions.CONTACTS}
              style={tabContentStyle}
            >
              <ContactsList
                contacts={rawTabDataByValue[projectMembersTabOptions.CONTACTS]}
                isLoading={isLoadingContacts}
              />
            </TabsComponent.Content>
          </ProjectMembersTabsContent>
        </TabsComponent.Root>
      </ProjectMembersTabsContainer>
    </ProjectMembersContainer>
  )
}

ProjectInfoPanelMembers.propTypes = {
  members: PropTypes.array,
  teams: PropTypes.array,
  contacts: PropTypes.array,
  isLoadingContacts: PropTypes.bool,
  isLoadingMembers: PropTypes.bool,
  isLoadingTeams: PropTypes.bool
}

export default ProjectInfoPanelMembers
